'use client';

import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Bell, ChevronRight, Menu, PanelLeftClose, Home } from 'lucide-react';
import Profile01 from './profile-01';
import { ModeToggle } from '../mode-toggle';
import { useGetUsersMeQuery } from '@/services';
import { TenantSelector } from '../tenants/tenant-selector';
import { useState, useMemo } from 'react';
import { CreateTenantDialog } from '../tenants/create-tenant-dialog';
import { NotificationsSettings } from '@/components/notifications/notification-settings';
import { useSidebar } from '@/contexts/sidebar-context';
import { Link, useRouterState } from '@tanstack/react-router';


interface BreadcrumbItem {
  label: string;
  href?: string;
}

// Route to breadcrumb label mapping
const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  devices: 'Devices',
  analytics: 'Analytics',
  locations: 'Locations',
  tenants: 'Members',
  settings: 'Settings',
  'ai-insights': 'AI Insights',
  scenes: 'Scenes',
  '3d-map': '3D Map',
};

export default function TopNav() {
  const { data } = useGetUsersMeQuery();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { toggle, isOpen, isMobile } = useSidebar();
  const router = useRouterState();

  // Generate breadcrumbs from current path
  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const pathname = router.location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    
    const crumbs: BreadcrumbItem[] = [
      { label: 'Dashboard', href: '/dashboard' },
    ];

    // Build breadcrumbs from path segments
    let currentPath = '';
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      currentPath += `/${segment}`;

      // Skip 'dashboard' as it's already added
      if (segment === 'dashboard') continue;

      // Check if this is a dynamic segment (like a UUID)
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
      
      if (isUUID) {
        // For UUIDs, show "Details" or similar
        crumbs.push({
          label: 'Details',
          href: currentPath,
        });
      } else {
        // Use mapped label or capitalize the segment
        const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        
        // Last segment doesn't need href (current page)
        const isLast = i === segments.length - 1;
        crumbs.push({
          label,
          href: isLast ? undefined : currentPath,
        });
      }
    }

    return crumbs;
  }, [router.location.pathname]);

  const user = data?.data;
  const name = user?.first_name || user?.email || 'anonymous';
  const role = user?.role;
  const avatar = undefined;
  const subscription = undefined;
  const profile = { name, role, avatar, subscription };
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  // Generate initials from user name or email
  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    // For email or single name, take first 1-2 characters
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <nav className="px-3 sm:px-6 flex items-center justify-between bg-white dark:bg-[#101014] border-b border-gray-200 dark:border-[#23232a] h-full shadow-sm dark:shadow-none">
      {/* Left section: Menu toggle + Breadcrumbs */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Sidebar toggle button */}
        <button
          type="button"
          onClick={toggle}
          className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-[#23232a] rounded-lg transition-colors"
          aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {isOpen && !isMobile ? (
            <PanelLeftClose className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          ) : (
            <Menu className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          )}
        </button>

        {/* Breadcrumbs */}
        <div className="font-medium text-sm hidden sm:flex items-center space-x-1 truncate max-w-[300px]">
          {breadcrumbs.map((item, index) => (
            <div key={item.label} className="flex items-center">
              {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500 mx-1" />}
              {item.href ? (
                <Link
                  to={item.href}
                  className="text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-gray-900 dark:text-white">{item.label}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tenant selector */}
      <div className="hidden md:block">
        <TenantSelector onCreateClick={() => setIsCreateDialogOpen(true)} />
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2 sm:gap-4 ml-auto sm:ml-0">
        <button
          type="button"
          className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-[#23232a] rounded-full transition-colors"
          aria-label="Notifications"
        >
          <Bell onClick={() => setIsNotificationsOpen(true)} className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>

        {/* <ModeToggle /> */}

        <DropdownMenu>
          <DropdownMenuTrigger className="focus:outline-none">
            <Avatar className="h-8 w-8 ring-2 ring-gray-200 dark:ring-[#23232a] cursor-pointer transition-shadow hover:ring-primary">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-[280px] sm:w-80 bg-background border-border rounded-lg shadow-lg"
          >
            <Profile01
              name={profile.name}
              role={profile.role}
              avatar={profile.avatar}
              subscription={profile.subscription}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Notification Settings Modal */}
      <NotificationsSettings
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        settingType='tenant'
      />

      {/* Create tenant dialog */}
      <CreateTenantDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
    </nav>
  );
}
